import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronRight,
  Download,
  Film,
  ImagePlus,
  Loader2,
  Mic2,
  Monitor,
  Pause,
  Play,
  RotateCcw,
  Save,
  ScrollText,
  Type,
  Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { PipelineOutlineDialog } from '@/components/learningTutorial/PipelineOutlineDialog';
import api from '@/lib/api';
import { useTutorialPipelineRunner } from '@/lib/learningTutorial/useTutorialPipelineRunner';
import { needsAudioPipelineRun, needsNarrationClipRerun } from '@/lib/learningTutorial/pipelineRunner';
import { suggestedArtifactGapsForProject } from '@/lib/learningTutorial/programmingContext';
import { LEARNING_TUTORIAL_SYNC_TIMEOUT_MS } from '@/lib/learningTutorial/apiTimeouts';
import { LearningPreviewStage } from '@/components/learningTutorial/LearningPreviewStage';
import { SceneMappedTimeline } from '@/components/learningTutorial/SceneMappedTimeline';
import { LogoBrandControls } from '@/components/learningTutorial/LogoBrandControls';
import { buildEditorLogo, logoModeFromEditor } from '@/lib/learningTutorial/logoBrand';
import { DEFAULT_CODE_PANEL, upcomingScreenplayEvents } from '@/lib/learningTutorial/layout';
import { MAX_LIST_LEVEL } from '@/lib/learningTutorial/listFormat';
import { framePlanAtPlayhead } from '@/lib/learningTutorial/framePlan';
import { buildExportDetails } from '@/lib/learningTutorial/exportDetails';
import {
  activeAtPlayhead,
  buildSceneMappedTimeline,
} from '@/lib/learningTutorial/sceneMappedTimeline';
import {
  exportAndUploadLearningTutorial,
  exportLearningTutorialFromPreview,
} from '@/lib/learningTutorial/exportFromPreview';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const mediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('blob:')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return `${BACKEND_URL}/${url}`;
};

export const LearningTutorialEditorPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [segments, setSegments] = useState([]);
  const [selectedScene, setSelectedScene] = useState(0);
  const [selectedBlock, setSelectedBlock] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const [logoMode, setLogoMode] = useState('none');
  const [logoText, setLogoText] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [ticker, setTicker] = useState('');
  const [renderJob, setRenderJob] = useState(null);
  const [exportMode, setExportMode] = useState('server');
  const [browserExporting, setBrowserExporting] = useState(false);
  const [browserProgress, setBrowserProgress] = useState('');
  const [previewMode, setPreviewMode] = useState('edit');
  const [selectedOverlayId, setSelectedOverlayId] = useState(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const activeMediaRef = useRef(null);
  const rafRef = useRef(null);
  const pollRef = useRef(null);
  const pendingPlayRef = useRef(false);
  const autoSyncRef = useRef(false);
  const [syncingAudio, setSyncingAudio] = useState(false);

  const applyProjectToEditor = useCallback((proj) => {
    setProject(proj);
    const ed = proj.editor || {};
    setSegments(ed.segments || []);
    if (proj.rendered_timeline?.total_duration) {
      setPlayhead((t) => Math.min(t, Number(proj.rendered_timeline.total_duration) || t));
    }
    setLogoMode(logoModeFromEditor(ed.logo));
    setLogoText(ed.logo?.text || '');
    setLogoUrl(ed.logo?.url || '');
    setTicker((ed.overlays && ed.overlays[0]?.text) || '');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/learning-tutorials/projects/${projectId}`);
      applyProjectToEditor(data.project);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load project');
    } finally {
      setLoading(false);
    }
  }, [projectId, applyProjectToEditor]);

  const pipeline = useTutorialPipelineRunner({
    api,
    projectId,
    project,
    setProject,
    onProjectRefreshed: applyProjectToEditor,
  });

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [load]);

  const rendered = project?.rendered_timeline;
  const duration = Number(rendered?.total_duration) || 30;

  const framePlan = useMemo(
    () => framePlanAtPlayhead(rendered, playhead),
    [rendered, playhead],
  );

  const suggestedArtifactGaps = useMemo(
    () => suggestedArtifactGapsForProject(project),
    [project],
  );

  const alignmentMode = rendered?.alignment_mode || project?.alignment_mode || 'pending';
  const screenplayDebug = useMemo(
    () => upcomingScreenplayEvents(rendered, playhead, 3),
    [rendered, playhead],
  );

  const previewStatus = project?.editor?.preview_status || 'draft';
  const isPreviewConfirmed = previewStatus === 'confirmed';
  const lifecycleStatus = project?.lifecycle_status
    || (project?.published_at
      ? 'published'
      : project?.final_video_url
        ? 'exported'
        : isPreviewConfirmed
          ? 'unpublished'
          : 'draft');
  const lifecycleLabel = {
    draft: 'Draft',
    unpublished: 'Unpublished',
    exported: 'Exported',
    published: 'Published',
  }[lifecycleStatus] || lifecycleStatus;

  const exportDetails = useMemo(
    () => buildExportDetails(project, segments),
    [project, segments],
  );

  const editorState = useMemo(
    () => ({
      logo: buildEditorLogo({
        mode: logoMode,
        text: logoText,
        url: mediaUrl(logoUrl) || logoUrl,
      }),
      overlays: ticker ? [{ text: ticker, x: 0.05, y: 0.92, opacity: 0.9 }] : [],
    }),
    [logoMode, logoText, logoUrl, ticker],
  );

  const videoSrc = mediaUrl(project?.final_video_url);
  const audioSrc = mediaUrl(project?.narration_audio_url);
  const useSyncedNarration = Boolean(audioSrc);

  const pausePlayback = useCallback(() => {
    audioRef.current?.pause();
    videoRef.current?.pause();
    activeMediaRef.current = null;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const syncVideoToTime = useCallback((t) => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;
    const target = Math.min(t, video.duration || t);
    if (Math.abs(video.currentTime - target) > 0.12) {
      video.currentTime = target;
    }
  }, [videoSrc]);

  const ensurePreviewNarration = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    const hasAudio = Boolean(project?.narration_audio_url);
    const hasWordAlign = (rendered?.alignment_mode || project?.alignment_mode) === 'word';
    if (hasAudio && hasWordAlign) return true;
    if (!rendered?.elements?.length) {
      if (!silent) toast.error('Generate a timeline before previewing with audio');
      return false;
    }
    setSyncingAudio(true);
    try {
      const { data } = await api.post(
        `/learning-tutorials/projects/${projectId}/preview-narration`,
        null,
        { timeout: LEARNING_TUTORIAL_SYNC_TIMEOUT_MS },
      );
      const next = {
        ...(data.project || project),
        narration_audio_url: data.narration_audio_url || data.project?.narration_audio_url,
        rendered_timeline: data.rendered_timeline || data.project?.rendered_timeline,
      };
      setProject(next);
      if (next.editor?.segments?.length) {
        setSegments(next.editor.segments);
      }
      if (data.rendered_timeline?.total_duration) {
        setPlayhead((t) => Math.min(t, Number(data.rendered_timeline.total_duration) || t));
      }
      if (!silent) {
        const mode = data.alignment_mode || data.rendered_timeline?.alignment_mode;
        if (mode) {
          const events = data.screenplay_event_count ?? data.rendered_timeline?.screenplay_event_count ?? 0;
          toast.success(`Narration synced (${mode} alignment · ${events} screenplay events)`);
        }
      }
      return Boolean(next.narration_audio_url);
    } catch (err) {
      if (!silent) {
        toast.error(err.response?.data?.detail || 'Could not generate preview narration');
      }
      return false;
    } finally {
      setSyncingAudio(false);
    }
  }, [project, projectId, rendered]);

  useEffect(() => {
    if (loading || !project || autoSyncRef.current) return;
    const hasAudio = Boolean(project.narration_audio_url);
    const hasWordAlign = (rendered?.alignment_mode || project?.alignment_mode) === 'word';
    if (hasAudio && hasWordAlign) return;
    if (!rendered?.elements?.length) return;
    autoSyncRef.current = true;
    ensurePreviewNarration({ silent: true });
  }, [loading, project, rendered, ensurePreviewNarration]);

  const startSyncedPlayback = useCallback(async () => {
    const t = Math.max(0, Math.min(playhead, duration));
    const audio = audioRef.current;
    const video = videoRef.current;

    if (useSyncedNarration && audio) {
      try {
        if (Math.abs(audio.currentTime - t) > 0.05) {
          audio.currentTime = Math.min(t, audio.duration || duration || t);
        }
        if (videoSrc && video) {
          video.muted = true;
          syncVideoToTime(t);
          if (previewMode === 'video') {
            try {
              await video.play();
            } catch {
              /* canvas may still show video frame */
            }
          }
        }
        await audio.play();
        activeMediaRef.current = audio;
        setPlaying(true);
      } catch {
        toast.error('Could not play narration. Click play again or check your speakers.');
        setPlaying(false);
      }
      return;
    }

    if (videoSrc && video) {
      try {
        video.muted = false;
        if (Math.abs(video.currentTime - t) > 0.05) {
          video.currentTime = Math.min(t, video.duration || duration || t);
        }
        await video.play();
        activeMediaRef.current = video;
        setPlaying(true);
      } catch {
        toast.error('Could not play video.');
        setPlaying(false);
      }
      return;
    }

    setPlaying(true);
  }, [playhead, duration, useSyncedNarration, videoSrc, previewMode, syncVideoToTime]);

  const togglePlay = useCallback(async () => {
    if (playing) {
      setPlaying(false);
      pausePlayback();
      return;
    }
    if (!audioSrc && !videoSrc && rendered?.elements?.length) {
      pendingPlayRef.current = true;
      const ok = await ensurePreviewNarration();
      if (!ok) {
        pendingPlayRef.current = false;
        return;
      }
      return;
    }
    if (!audioSrc && !videoSrc) {
      setPlaying(true);
      return;
    }
    await startSyncedPlayback();
  }, [
    playing,
    audioSrc,
    videoSrc,
    rendered,
    pausePlayback,
    ensurePreviewNarration,
    startSyncedPlayback,
  ]);

  useEffect(() => {
    if (!pendingPlayRef.current || !audioSrc) return;
    pendingPlayRef.current = false;
    startSyncedPlayback();
  }, [audioSrc, startSyncedPlayback]);

  useEffect(() => {
    if (!playing) {
      pausePlayback();
      return undefined;
    }

    const audio = audioRef.current;
    if (useSyncedNarration && audio) {
      const onTime = () => {
        const time = audio.currentTime;
        setPlayhead(time);
        syncVideoToTime(time);
      };
      const onEnd = () => {
        setPlaying(false);
        activeMediaRef.current = null;
      };
      audio.addEventListener('timeupdate', onTime);
      audio.addEventListener('ended', onEnd);
      return () => {
        audio.removeEventListener('timeupdate', onTime);
        audio.removeEventListener('ended', onEnd);
      };
    }

    const video = videoRef.current;
    if (videoSrc && video && activeMediaRef.current === video) {
      const onTime = () => setPlayhead(video.currentTime);
      const onEnd = () => {
        setPlaying(false);
        activeMediaRef.current = null;
      };
      video.addEventListener('timeupdate', onTime);
      video.addEventListener('ended', onEnd);
      return () => {
        video.removeEventListener('timeupdate', onTime);
        video.removeEventListener('ended', onEnd);
      };
    }

    const start = performance.now() - playhead * 1000;
    const tick = (now) => {
      const time = (now - start) / 1000;
      if (time >= duration) {
        setPlaying(false);
        setPlayhead(0);
        return;
      }
      setPlayhead(time);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, duration, useSyncedNarration, videoSrc, playhead, pausePlayback, syncVideoToTime]);

  const seekTo = (t) => {
    const clamped = Math.max(0, Math.min(t, duration));
    setPlayhead(clamped);
    setPlaying(false);
    pausePlayback();
    if (audioRef.current) audioRef.current.currentTime = clamped;
    if (videoRef.current) videoRef.current.currentTime = clamped;
  };

  const mappedTimeline = useMemo(
    () => buildSceneMappedTimeline({ segments, duration, rendered }),
    [segments, duration, rendered],
  );

  useEffect(() => {
    if (!playing || !mappedTimeline.scenes.length) return;
    const { sceneIndex, blockIndex } = activeAtPlayhead(
      mappedTimeline,
      playhead,
      rendered?.elements,
      rendered,
    );
    if (sceneIndex >= 0) setSelectedScene(sceneIndex);
    if (blockIndex >= 0) setSelectedBlock(blockIndex);
  }, [playing, playhead, mappedTimeline]);

  const findBlockIndex = (seg, blockId) => (seg?.blocks || []).findIndex((b) => b.block_id === blockId);

  const handleSelectOverlay = (overlayId) => {
    setSelectedOverlayId(overlayId);
    if (!overlayId || overlayId === '__code_panel__') return;
    const seg = segments[selectedScene];
    const bi = findBlockIndex(seg, overlayId);
    if (bi >= 0) setSelectedBlock(bi);
  };

  const handleLayoutChange = ({ sceneIndex, blockId, layout, codePanel }) => {
    setSegments((prev) =>
      prev.map((seg, i) => {
        if (i !== sceneIndex) return seg;
        if (codePanel) {
          return {
            ...seg,
            code_panel_layout: { ...(seg.code_panel_layout || DEFAULT_CODE_PANEL), ...layout },
          };
        }
        return {
          ...seg,
          blocks: (seg.blocks || []).map((b) =>
            b.block_id === blockId ? { ...b, layout: { ...(b.layout || {}), ...layout } } : b,
          ),
        };
      }),
    );
  };

  const handleOverlayTextChange = (blockId, text) => {
    const bi = findBlockIndex(segments[selectedScene], blockId);
    if (bi >= 0) updateBlockField('screen_text', text);
  };

  const handleOverlayImageUpload = async (blockId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', 'image');
    try {
      const { data } = await api.post('/learning-tutorials/upload', fd);
      const bi = findBlockIndex(segments[selectedScene], blockId);
      if (bi >= 0) {
        setSegments((prev) =>
          prev.map((seg, si) => {
            if (si !== selectedScene) return seg;
            const blocks = (seg.blocks || []).map((b, bj) =>
              bj === bi ? { ...b, image_url: data.url, type: 'image' } : b,
            );
            return { ...seg, blocks };
          }),
        );
        toast.success('Image added');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const currentSeg = segments[selectedScene];
  const currentBlock = currentSeg?.blocks?.[selectedBlock];

  const updateBlockField = (field, value) => {
    setSegments((prev) => {
      const next = prev.map((s, si) => {
        if (si !== selectedScene) return s;
        const blocks = (s.blocks || []).map((b, bi) => (
          bi === selectedBlock ? { ...b, [field]: value } : b
        ));
        const narration = blocks.map((b) => b.narration).filter(Boolean).join(' ');
        return { ...s, blocks, narration };
      });
      return next;
    });
  };

  const saveSegments = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/learning-tutorials/projects/${projectId}/segments`, {
        segments,
      });
      setSegments(data.segments || segments);
      setProject((p) => ({
        ...p,
        rendered_timeline: data.rendered_timeline,
        frames_preview: data.frames_preview,
        editor: {
          ...(p?.editor || {}),
          segments: data.segments,
          preview_status: data.preview_status ?? p?.editor?.preview_status ?? 'draft',
          confirmed_at: data.confirmed_at ?? p?.editor?.confirmed_at ?? null,
        },
      }));
      toast.success('Scene script & screen text saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEditorLogoModeChange = (m) => {
    setLogoMode(m);
    if (m === 'none') {
      setLogoText('');
      setLogoUrl('');
    } else if (m === 'text') setLogoUrl('');
    else if (m === 'image') setLogoText('');
  };

  const saveEditorMeta = async () => {
    await api.patch(`/learning-tutorials/projects/${projectId}/editor`, {
      logo: buildEditorLogo({ mode: logoMode, text: logoText, url: logoUrl }),
      overlays: ticker ? [{ text: ticker, x: 0.05, y: 0.92, opacity: 0.9 }] : [],
    });
  };

  const confirmPreview = async () => {
    setSaving(true);
    try {
      await saveSegments();
      await saveEditorMeta();
      const { data } = await api.post(`/learning-tutorials/projects/${projectId}/confirm-preview`);
      setProject((p) => ({
        ...p,
        editor: {
          ...(p?.editor || {}),
          preview_status: 'confirmed',
          confirmed_at: data.confirmed_at,
        },
        rendered_timeline: p?.rendered_timeline,
      }));
      toast.success('Visual plan confirmed — you can export');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not confirm preview');
    } finally {
      setSaving(false);
    }
  };

  const unconfirmPreview = async () => {
    try {
      await api.post(`/learning-tutorials/projects/${projectId}/unconfirm-preview`);
      setProject((p) => ({
        ...p,
        editor: { ...(p?.editor || {}), preview_status: 'draft', confirmed_at: null },
      }));
      toast.message('Preview unlocked for editing');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not unlock preview');
    }
  };

  const exportServer = async () => {
    if (!isPreviewConfirmed) {
      toast.error('Confirm the visual plan before server export');
      return;
    }
    setSaving(true);
    try {
      await saveSegments();
      await saveEditorMeta();
      const { data } = await api.post(`/learning-tutorials/projects/${projectId}/render`);
      setRenderJob({ status: 'running', progress: 0, message: 'Queued', job_id: data.job_id });
      pollRef.current = setInterval(async () => {
        const { data: j } = await api.get(`/learning-tutorials/projects/${projectId}/jobs/${data.job_id}`);
        setRenderJob(j.job);
        if (j.job.status === 'completed') {
          clearInterval(pollRef.current);
          toast.success('Export complete');
          load();
        } else if (j.job.status === 'failed') {
          clearInterval(pollRef.current);
          toast.error(j.job.error_message || 'Export failed');
        }
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start server export');
    } finally {
      setSaving(false);
    }
  };

  const exportBrowser = async (uploadAfter) => {
    setBrowserExporting(true);
    setBrowserProgress('Starting…');
    try {
      await saveSegments();
      await saveEditorMeta();
      const latest = await api.get(`/learning-tutorials/projects/${projectId}`);
      const proj = latest.data.project;
      const segs = proj.editor?.segments || segments;
      const opts = {
        rendered: proj.rendered_timeline,
        segments: segs,
        editorState,
        narrationAudioUrl: proj.narration_audio_url,
        backendUrl: BACKEND_URL,
        filename: (proj.title || 'lesson').replace(/\s+/g, '-'),
        onProgress: setBrowserProgress,
        download: !uploadAfter,
      };
      if (uploadAfter) {
        await exportAndUploadLearningTutorial({ projectId, api, ...opts });
        toast.success('Video uploaded to project');
        load();
      } else {
        await exportLearningTutorialFromPreview(opts);
        toast.success('Download started');
      }
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || 'Browser export failed');
    } finally {
      setBrowserExporting(false);
      setBrowserProgress('');
    }
  };

  const handleExport = async () => {
    if (!isPreviewConfirmed) {
      toast.error('Confirm the visual plan before exporting');
      return;
    }
    if (exportMode === 'browser') {
      await exportBrowser(false);
    } else {
      await exportServer();
    }
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', 'logo');
    const { data } = await api.post('/learning-tutorials/upload', fd);
    setLogoUrl(data.url);
    if (logoMode === 'none') setLogoMode('image');
    else if (logoMode === 'text') setLogoMode('both');
    toast.success('Logo uploaded');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header className="border-b border-slate-800 px-4 py-3 flex items-center justify-between shrink-0">
        <div>
          <h1 className="font-semibold flex items-center gap-2 flex-wrap">
            {project?.title || 'Tutorial editor'}
            <span className="text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full border border-slate-600 text-slate-400">
              {lifecycleLabel}
            </span>
          </h1>
          <p className="text-xs text-slate-500">Scenes (script chapters) — one full video export, no per-scene renders</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" size="sm" onClick={() => navigate('/script-audio-align', { state: { prefill: { mode: 'topic' } } })}>Back</Button>
          {pipeline.hasPipeline ? (
            <>
              {needsNarrationClipRerun(project) ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  disabled={pipeline.status === 'working'}
                  onClick={() => pipeline.runPipelineStep('narration_audio')}
                  data-testid="editor-rerun-narration-audio"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  Re-run TTS
                </Button>
              ) : null}
              {pipeline.showResumeActions ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                    disabled={pipeline.status === 'working'}
                    onClick={pipeline.retryStep}
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                    Retry step
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                    disabled={pipeline.status === 'working'}
                    onClick={pipeline.retryAndContinue}
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                    Retry &amp; continue
                  </Button>
                </>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                onClick={() => pipeline.setOutlineOpen(true)}
              >
                <ScrollText className="w-4 h-4 mr-1" />
                Pipeline outline
              </Button>
            </>
          ) : null}
          <Button size="sm" disabled={saving} onClick={saveSegments}>
            <Save className="w-4 h-4 mr-1" />
            Save edits
          </Button>
          {!isPreviewConfirmed ? (
            <Button size="sm" variant="secondary" disabled={saving || !rendered} onClick={confirmPreview}>
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Confirm visual plan
            </Button>
          ) : (
            <Button size="sm" variant="outline" disabled={saving} onClick={unconfirmPreview}>
              <Undo2 className="w-4 h-4 mr-1" />
              Unlock edits
            </Button>
          )}
          <Button
            size="sm"
            className="bg-emerald-600"
            disabled={saving || browserExporting || !isPreviewConfirmed}
            onClick={handleExport}
          >
            <TwinneeLogo className="w-4 h-4 mr-1" />
            Export video
          </Button>
        </div>
      </header>

      {pipeline.hasPipeline && (pipeline.status !== 'ready' || needsAudioPipelineRun(project)) ? (
        <div className="px-4 py-2 border-b border-amber-900/40 bg-amber-950/25 space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-xs text-amber-200/90 flex-wrap">
            <span>
              {needsAudioPipelineRun(project)
                ? 'Trainer script ready — generate audio and sync artifacts to narration'
                : `Pipeline ${pipeline.progressPct}% complete`}
              {pipeline.status === 'working' ? ' — running…' : ''}
              {pipeline.status === 'error' ? ' — step failed' : ''}
            </span>
            <div className="flex items-center gap-2">
              {needsAudioPipelineRun(project) ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                  disabled={pipeline.status === 'working'}
                  onClick={() => pipeline.executeResume('narration_audio', { continueAfter: true })}
                  data-testid="editor-run-audio-pipeline"
                >
                  <Mic2 className="w-3.5 h-3.5 mr-1" />
                  Generate audio &amp; sync artifacts
                </Button>
              ) : null}
              <button
                type="button"
                className="text-amber-400 hover:text-amber-300 underline-offset-2 hover:underline"
                onClick={() => pipeline.setOutlineOpen(true)}
              >
                View outline
              </button>
            </div>
          </div>
          <Progress value={pipeline.progressPct} className="h-1.5" />
          {pipeline.pipelineError ? (
            <p className="text-[11px] text-red-300/90">{pipeline.pipelineError}</p>
          ) : null}
        </div>
      ) : null}

      {suggestedArtifactGaps.length > 0 && Number(project?.orchestration?.pipeline_pass) >= 2 ? (
        <div className="px-4 py-2 border-b border-violet-900/40 bg-violet-950/20 text-xs text-violet-200/90 flex flex-wrap items-center justify-between gap-2">
          <span>
            Suggested code or dry-run artifacts were skipped in Pass 2 ({suggestedArtifactGaps.length} concept
            {suggestedArtifactGaps.length === 1 ? '' : 's'}).
            {project?.intake?.include_code !== true
              ? ' Enable Include code and re-run Pass 2 for walkthrough suggestions.'
              : ' Re-run Pass 2 to let the LLM add optional walkthroughs.'}
          </span>
          <button
            type="button"
            className="text-violet-300 hover:text-violet-200 underline-offset-2 hover:underline shrink-0"
            onClick={() => pipeline.setOutlineOpen(true)}
          >
            View Pass 2 outline
          </button>
        </div>
      ) : null}

      {!isPreviewConfirmed && rendered && (
        <div className="px-4 py-2 border-b border-amber-900/50 bg-amber-950/30 text-amber-200 text-xs">
          Save your edits, scrub the preview, then confirm the visual plan before exporting.
        </div>
      )}

      {(renderJob?.status === 'running' || browserExporting) && (
        <div className="px-4 py-2 border-b border-slate-800 space-y-1">
          <Progress value={renderJob?.progress || (browserExporting ? 50 : 0)} />
          <p className="text-xs text-slate-500">
            {browserExporting ? browserProgress : (renderJob?.message || 'Exporting…')}
          </p>
        </div>
      )}

      <div className="flex-1 flex min-h-0 overflow-hidden">
        <section className="flex-1 flex flex-col min-w-0 border-r border-slate-800">
          <div className="p-4 flex-1 flex flex-col gap-3 min-h-0">
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                variant={previewMode === 'edit' ? 'default' : 'outline'}
                onClick={() => {
                  setPreviewMode('edit');
                  setPlaying(false);
                  pausePlayback();
                }}
              >
                Edit on preview
              </Button>
              {videoSrc && (
                <Button
                  size="sm"
                  variant={previewMode === 'video' ? 'default' : 'outline'}
                  onClick={() => {
                    setPreviewMode('video');
                    setPlaying(false);
                    pausePlayback();
                  }}
                >
                  Watch rendered
                </Button>
              )}
            </div>
            {audioSrc && (
              <audio
                ref={audioRef}
                src={audioSrc}
                preload="auto"
                crossOrigin="anonymous"
                className="hidden"
              />
            )}
            <LearningPreviewStage
              rendered={rendered}
              segments={segments}
              playhead={playhead}
              playing={playing}
              editorState={editorState}
              selectedOverlayId={selectedOverlayId}
              onSelectOverlay={handleSelectOverlay}
              onLayoutChange={handleLayoutChange}
              onTextChange={handleOverlayTextChange}
              onImageUpload={handleOverlayImageUpload}
              showVideo={previewMode === 'video'}
              videoSrc={videoSrc}
              videoRef={videoRef}
              onVideoTimeUpdate={() => {
                if (playing && activeMediaRef.current === videoRef.current && !useSyncedNarration) {
                  setPlayhead(videoRef.current?.currentTime || 0);
                }
              }}
              videoMuted={useSyncedNarration}
            />

            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={togglePlay}
                disabled={syncingAudio || (!audioSrc && !videoSrc && !rendered?.elements?.length)}
              >
                {syncingAudio ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : playing ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
              </Button>
              {syncingAudio ? (
                <span className="text-xs text-slate-500">Syncing TTS + WhisperX…</span>
              ) : !audioSrc && rendered?.elements?.length ? (
                <span className="text-xs text-amber-400/90">Narration not synced yet</span>
              ) : null}
              <input
                type="range"
                className="flex-1"
                min={0}
                max={duration}
                step={0.033}
                value={playhead}
                onChange={(e) => seekTo(Number(e.target.value))}
              />
              <span className="text-xs text-slate-500 tabular-nums">
                {playhead.toFixed(1)}s / {duration.toFixed(1)}s
              </span>
              <span
                className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border border-slate-600 text-slate-400"
                title="WhisperX word-level alignment (required for preview and export)"
              >
                {alignmentMode}
              </span>
            </div>

            {screenplayDebug.length > 0 ? (
              <div className="shrink-0 rounded border border-slate-700/80 bg-slate-900/60 px-3 py-2 text-[11px] text-slate-400">
                <p className="font-medium text-slate-300 mb-1">Upcoming screenplay events</p>
                <ul className="space-y-0.5 font-mono">
                  {screenplayDebug.map((evt) => (
                    <li key={evt.event_id}>
                      {Number(evt.global_time).toFixed(2)}s · {evt.action} · {evt.element_id}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="shrink-0">
              <SceneMappedTimeline
                segments={segments}
                rendered={rendered}
                duration={duration}
                playhead={playhead}
                selectedScene={selectedScene}
                selectedBlock={selectedBlock}
                onSeek={seekTo}
                onSelectScene={(i) => {
                  setSelectedScene(i);
                  setSelectedBlock(0);
                }}
                onSelectBlock={(sceneIdx, blockIdx) => {
                  setSelectedScene(sceneIdx);
                  setSelectedBlock(blockIdx);
                }}
              />
              <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                <div className="flex items-center gap-2 rounded border border-slate-700 px-2 py-1.5 bg-slate-900/80">
                  <Film className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Video</span>
                  <span className="ml-auto text-slate-500">{videoSrc ? 'Rendered' : 'Preview canvas'}</span>
                </div>
                <div className="flex items-center gap-2 rounded border border-slate-700 px-2 py-1.5 bg-slate-900/80">
                  <Mic2 className="w-3.5 h-3.5 text-sky-400" />
                  <span>Narration</span>
                  {audioSrc && <audio src={audioSrc} controls className="h-6 max-w-[140px]" />}
                </div>
              </div>
              <div className="flex gap-1 overflow-x-auto pb-1">
                {segments.map((seg, i) => (
                  <button
                    key={seg.id}
                    type="button"
                    onClick={() => {
                      setSelectedScene(i);
                      setSelectedBlock(0);
                      setSelectedOverlayId(null);
                      seekTo(seg.start_time || 0);
                    }}
                    className={`shrink-0 px-3 py-1.5 rounded text-xs border ${
                      i === selectedScene
                        ? 'border-emerald-500 bg-emerald-950 text-emerald-200'
                        : 'border-slate-700 bg-slate-900 text-slate-400'
                    }`}
                  >
                    {seg.title || `Scene ${i + 1}`}
                    <span className="block text-[10px] opacity-70">{(seg.duration || 0).toFixed(1)}s</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <aside className="w-[400px] shrink-0 flex flex-col overflow-hidden border-l border-slate-800">
          <div className="p-4 border-b border-slate-800 flex items-center gap-2">
            <Monitor className="w-4 h-4 text-emerald-400" />
            <h2 className="text-sm font-semibold">Scene editor</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {currentSeg ? (
              <>
                <div>
                  <Label className="text-xs">Scene title</Label>
                  <Input
                    className="mt-1 bg-slate-800"
                    value={currentSeg.title || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSegments((prev) => prev.map((s, i) => (i === selectedScene ? { ...s, title: v } : s)));
                    }}
                  />
                </div>
                <div>
                  <Label className="text-xs">Scene narration (full script)</Label>
                  <Textarea
                    className="mt-1 bg-slate-800 text-sm"
                    rows={4}
                    value={currentSeg.narration || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSegments((prev) => prev.map((s, i) => (i === selectedScene ? { ...s, narration: v } : s)));
                    }}
                  />
                </div>
                <div>
                  <Label className="text-xs mb-2 block">On-screen blocks</Label>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {(currentSeg.blocks || []).map((b, bi) => (
                      <button
                        key={b.block_id || bi}
                        type="button"
                        onClick={() => {
                          setSelectedBlock(bi);
                          setSelectedOverlayId(b.block_id);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs border flex items-center gap-1 ${
                          bi === selectedBlock ? 'border-emerald-600 bg-emerald-950/50' : 'border-slate-700'
                        }`}
                      >
                        <ChevronRight className="w-3 h-3 shrink-0" />
                        <span className="text-emerald-400">{b.type}</span>
                        {(b.list_level ?? 0) > 0 && (
                          <span className="text-[10px] text-slate-500 shrink-0">L{b.list_level}</span>
                        )}
                        <span
                          className="truncate text-slate-400"
                          style={{ paddingLeft: `${Math.min(b.list_level || 0, 4) * 10}px` }}
                        >
                          {b.screen_text}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                {currentBlock && (
                  <>
                    <div>
                      <Label className="text-xs">Screen text ({currentBlock.type}) — also editable on preview</Label>
                      <Textarea
                        className="mt-1 bg-slate-800 font-mono text-sm"
                        rows={4}
                        value={currentBlock.screen_text || ''}
                        onChange={(e) => updateBlockField('screen_text', e.target.value)}
                      />
                    </div>
                    {['bullet', 'explanation'].includes(currentBlock.type) && (
                      <div>
                        <Label className="text-xs">List nesting (0 = top level, higher = indented subtopic)</Label>
                        <select
                          className="mt-1 w-full rounded border border-slate-600 bg-slate-800 px-2 py-1.5 text-sm"
                          value={Number(currentBlock.list_level) || 0}
                          onChange={(e) => updateBlockField('list_level', Number(e.target.value))}
                        >
                          {Array.from({ length: MAX_LIST_LEVEL + 1 }, (_, i) => (
                            <option key={i} value={i}>
                              Level {i}
                              {i === 0 ? ' — main bullet' : ` — nested ${'·'.repeat(i)}`}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-[10px] text-slate-500">
                          Each level gets its own narration beat; parent bullets stay on screen.
                        </p>
                      </div>
                    )}
                    {currentBlock.type === 'image' && (
                      <div>
                        <Label className="text-xs">Image</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          className="mt-1"
                          onChange={(e) =>
                            e.target.files?.[0] &&
                            handleOverlayImageUpload(currentBlock.block_id, e.target.files[0])
                          }
                        />
                        {currentBlock.image_url && (
                          <div className="mt-2 max-h-24 rounded bg-white border border-slate-700 overflow-hidden inline-block">
                            <img
                              src={mediaUrl(currentBlock.image_url)}
                              alt=""
                              className="max-h-24 object-contain"
                              onError={(e) => {
                                e.currentTarget.remove();
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-500">
                      Layout: x={currentBlock.layout?.x ?? '—'} y={currentBlock.layout?.y ?? '—'}
                    </div>
                    <div>
                      <Label className="text-xs">Block narration</Label>
                      <Textarea
                        className="mt-1 bg-slate-800 text-sm"
                        rows={3}
                        value={currentBlock.narration || ''}
                        onChange={(e) => updateBlockField('narration', e.target.value)}
                      />
                    </div>
                  </>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">Generate a timeline first to edit scenes.</p>
            )}

            {framePlan && (
              <div className="rounded border border-slate-700 bg-slate-900/80 p-3 space-y-2">
                <h3 className="text-xs font-semibold text-sky-300">On screen @ {framePlan.time?.toFixed?.(1) ?? playhead.toFixed(1)}s</h3>
                <p className="text-[10px] text-slate-500">
                  Layers stay visible within each scene; new text stacks while narration explains each beat.
                </p>
                {framePlan.narration_active && (
                  <p className="text-xs text-slate-400 italic line-clamp-3">
                    Narration: {framePlan.narration_active}
                  </p>
                )}
                {framePlan.narrator_cue && (
                  <div className="rounded border border-amber-800/50 bg-amber-950/30 px-2 py-1.5 space-y-0.5">
                    <p className="text-[10px] text-amber-400/80 uppercase tracking-wide">
                      Trainer cue
                      {framePlan.narrator_cue.artifact_type ? ` · ${framePlan.narrator_cue.artifact_type}` : ''}
                      {framePlan.narrator_cue.step_index != null ? ` · step ${framePlan.narrator_cue.step_index + 1}` : ''}
                    </p>
                    <p className="text-xs text-amber-100 leading-snug">
                      {framePlan.narrator_cue.concise_text}
                    </p>
                  </div>
                )}
                <ul className="text-xs space-y-1 max-h-28 overflow-y-auto">
                  {(framePlan.layers || []).map((layer) => (
                    <li key={layer.id} className="flex gap-2 items-start">
                      <span className={`shrink-0 px-1 rounded text-[10px] ${
                        layer.state === 'entering' ? 'bg-emerald-800 text-emerald-200' :
                        layer.state === 'exiting' ? 'bg-amber-900 text-amber-200' :
                        'bg-slate-800 text-slate-400'
                      }`}>
                        {layer.state || 'visible'}
                      </span>
                      <span className="text-emerald-500 shrink-0">{layer.type}</span>
                      <span className="text-slate-300 truncate">{layer.content}</span>
                    </li>
                  ))}
                </ul>
                {(framePlan.code_panel?.lines?.length > 0) && (
                  <div className="text-[10px] font-mono text-slate-400 border-t border-slate-800 pt-2">
                    Code panel ({framePlan.code_panel.lines.length} lines)
                  </div>
                )}
                {(framePlan.entering?.length > 0 || framePlan.exiting?.length > 0) && (
                  <p className="text-[10px] text-slate-500">
                    {framePlan.entering?.length ? `+${framePlan.entering.join(', ')}` : ''}
                    {framePlan.exiting?.length ? ` −${framePlan.exiting.join(', ')}` : ''}
                  </p>
                )}
              </div>
            )}

            <div className="rounded border border-slate-700 bg-slate-900/80 p-3 space-y-3">
              <h3 className="text-xs font-semibold text-violet-300">Export</h3>
              {exportDetails && (
                <dl className="text-[10px] text-slate-400 grid grid-cols-2 gap-x-2 gap-y-1">
                  <dt>Resolution</dt>
                  <dd>{exportDetails.width}×{exportDetails.height}</dd>
                  <dt>Duration</dt>
                  <dd>{exportDetails.total_duration.toFixed(1)}s @ {exportDetails.fps} fps</dd>
                  <dt>Frames</dt>
                  <dd>{exportDetails.total_frames}</dd>
                  <dt>Scenes / blocks</dt>
                  <dd>{exportDetails.scene_count} / {exportDetails.block_count}</dd>
                  <dt>Manifest samples</dt>
                  <dd>{exportDetails.manifest_sampled_frames ?? '—'} (stride {exportDetails.manifest_stride ?? '—'})</dd>
                  <dt>Narration</dt>
                  <dd>{exportDetails.narration_char_count} chars{exportDetails.reuse_narration_audio ? ' · reuse audio' : ''}</dd>
                  <dt>Status</dt>
                  <dd className={isPreviewConfirmed ? 'text-emerald-400' : 'text-amber-400'}>
                    {isPreviewConfirmed ? `Confirmed ${exportDetails.confirmed_at ? new Date(exportDetails.confirmed_at).toLocaleString() : ''}` : 'Draft'}
                  </dd>
                </dl>
              )}
              <div className="space-y-2">
                <Label className="text-xs">Export method</Label>
                <div className="flex flex-col gap-1.5 text-xs">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="exportMode"
                      checked={exportMode === 'server'}
                      onChange={() => setExportMode('server')}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="text-slate-200 font-medium">Server</span>
                      <span className="block text-slate-500">Single PIL pass + FFmpeg. Faster for long lessons.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="exportMode"
                      checked={exportMode === 'browser'}
                      onChange={() => setExportMode('browser')}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="text-slate-200 font-medium">Browser</span>
                      <span className="block text-slate-500">Matches preview canvas (WYSIWYG). Uses FFmpeg in this tab.</span>
                    </span>
                  </label>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  size="sm"
                  className="bg-emerald-600 w-full"
                  disabled={!isPreviewConfirmed || saving || browserExporting}
                  onClick={handleExport}
                >
                  <TwinneeLogo className="w-4 h-4 mr-1" />
                  Export ({exportMode === 'server' ? 'server' : 'browser'})
                </Button>
                {exportMode === 'browser' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    disabled={!isPreviewConfirmed || saving || browserExporting}
                    onClick={() => exportBrowser(true)}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Export in browser &amp; save to project
                  </Button>
                )}
              </div>
            </div>

            <hr className="border-slate-800" />
            <div>
              <h3 className="text-xs font-semibold flex items-center gap-2 mb-2">
                <ImagePlus className="w-3.5 h-3.5" /> Logo / brand
              </h3>
              <LogoBrandControls
                compact
                mode={logoMode}
                onModeChange={handleEditorLogoModeChange}
                text={logoText}
                onTextChange={setLogoText}
                imageUrl={mediaUrl(logoUrl)}
                onImageUrlChange={setLogoUrl}
                onFileSelect={uploadLogo}
              />
            </div>
            <div>
              <h3 className="text-xs font-semibold flex items-center gap-2 mb-2">
                <Type className="w-3.5 h-3.5" /> Ticker / overlay
              </h3>
              <Textarea value={ticker} onChange={(e) => setTicker(e.target.value)} className="bg-slate-800" rows={2} />
            </div>
          </div>
        </aside>
      </div>

      <PipelineOutlineDialog
        open={pipeline.outlineOpen}
        onOpenChange={pipeline.setOutlineOpen}
        currentStepId={pipeline.currentStep}
        pipelineStatus={pipeline.status}
        orchestration={pipeline.orchestration}
        project={project}
        initialStepId={pipeline.currentStep}
        resumeStepId={pipeline.resumeStepId}
        pipelineError={pipeline.pipelineError}
        resumeLoading={pipeline.status === 'working'}
        onRetryStep={pipeline.showResumeActions ? pipeline.retryStep : undefined}
        onRetryAndContinue={pipeline.showResumeActions ? pipeline.retryAndContinue : undefined}
        onRunStep={pipeline.runPipelineStep}
      />
    </div>
  );
};

export default LearningTutorialEditorPage;
